/**
 * Image Cache Service
 * Provides caching functionality for generated and historical images
 * Includes preloading, cache management, and performance optimizations
 */

interface CachedImage {
  url: string;
  blob: Blob;
  timestamp: number;
  size: number;
}

interface CacheStats {
  totalSize: number;
  itemCount: number;
  hitRate: number;
  totalRequests: number;
  cacheHits: number;
}

class ImageCacheService {
  private cache: Map<string, CachedImage> = new Map();
  private preloadQueue: Set<string> = new Set();
  private readonly MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB
  private readonly MAX_ITEMS = 200;
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

  // Performance tracking
  private totalRequests = 0;
  private cacheHits = 0;

  /**
   * Get an image from cache or fetch it with retry logic
   */
  async getImage(url: string, retries: number = 3): Promise<string> {
    this.totalRequests++;

    // Check cache first
    const cached = this.cache.get(url);
    if (cached && this.isValidCacheEntry(cached)) {
      this.cacheHits++;
      return URL.createObjectURL(cached.blob);
    }

    // Try to fetch and cache the image with retry logic
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          mode: 'cors', // Explicitly set CORS mode
          cache: 'no-cache', // Don't use cached responses that might be blocked
        });
        
        if (!response.ok) {
          if (response.status === 404) {
            console.warn('Image not found (404):', url);
            return url;
          }
          throw new Error(`Failed to fetch image: ${response.status}`);
        }

        const blob = await response.blob();

        // Validate image type - handle opaque responses
        if (blob.type === '' || blob.size === 0) {
          // This might be an opaque response due to CORS issues
          console.warn('Received opaque response, likely due to CORS issues:', url);
          return url;
        }

        if (!blob.type.startsWith('image/')) {
          throw new Error('Invalid image type');
        }

        // Cache the image
        this.cacheImage(url, blob);

        return URL.createObjectURL(blob);
      } catch (error) {
        if (attempt === retries) {
          console.warn(`Failed to fetch image after ${retries} attempts, falling back to direct URL:`, url, error);
          // Return original URL as fallback - let the browser handle it directly
          return url;
        } else {
          console.warn(`Image fetch attempt ${attempt} failed, retrying...`, url);
          // Wait before retry (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    }

    return url;
  }

  /**
   * Preload images in the background
   */
  async preloadImages(urls: string[]): Promise<void> {
    const newUrls = urls.filter(
      url => !this.cache.has(url) && !this.preloadQueue.has(url)
    );

    if (newUrls.length === 0) return;

    // Add to preload queue
    newUrls.forEach(url => this.preloadQueue.add(url));

    // Preload in batches to avoid overwhelming the network
    const batchSize = 3;
    for (let i = 0; i < newUrls.length; i += batchSize) {
      const batch = newUrls.slice(i, i + batchSize);

      await Promise.allSettled(
        batch.map(async url => {
          try {
            await this.getImage(url);
          } catch (error) {
            // Only warn for non-404 and non-CORS errors to reduce noise
            if (!error.message?.includes('404') && !error.message?.includes('CORS')) {
              console.warn('Failed to preload image:', url, error);
            }
          } finally {
            this.preloadQueue.delete(url);
          }
        })
      );

      // Small delay between batches to prevent blocking
      if (i + batchSize < newUrls.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  }

  /**
   * Cache an image blob
   */
  private cacheImage(url: string, blob: Blob): void {
    const size = blob.size;

    // Check if we need to make space
    this.ensureCacheSpace(size);

    const cacheEntry: CachedImage = {
      url,
      blob,
      timestamp: Date.now(),
      size,
    };

    this.cache.set(url, cacheEntry);
  }

  /**
   * Ensure there's enough space in the cache
   */
  private ensureCacheSpace(newItemSize: number): void {
    // Remove expired items first
    this.cleanupExpiredItems();

    // If still over limit, remove oldest items
    while (
      this.getTotalCacheSize() + newItemSize > this.MAX_CACHE_SIZE ||
      this.cache.size >= this.MAX_ITEMS
    ) {
      this.removeOldestItem();
    }
  }

  /**
   * Remove expired cache items
   */
  private cleanupExpiredItems(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    this.cache.forEach((item, key) => {
      if (now - item.timestamp > this.CACHE_DURATION) {
        expiredKeys.push(key);
      }
    });

    expiredKeys.forEach(key => {
      const item = this.cache.get(key);
      if (item) {
        URL.revokeObjectURL(URL.createObjectURL(item.blob));
        this.cache.delete(key);
      }
    });
  }

  /**
   * Remove the oldest cache item
   */
  private removeOldestItem(): void {
    let oldestKey: string | null = null;
    let oldestTimestamp = Date.now();

    this.cache.forEach((item, key) => {
      if (item.timestamp < oldestTimestamp) {
        oldestTimestamp = item.timestamp;
        oldestKey = key;
      }
    });

    if (oldestKey) {
      const item = this.cache.get(oldestKey);
      if (item) {
        URL.revokeObjectURL(URL.createObjectURL(item.blob));
        this.cache.delete(oldestKey);
      }
    }
  }

  /**
   * Check if a cache entry is still valid
   */
  private isValidCacheEntry(entry: CachedImage): boolean {
    return Date.now() - entry.timestamp < this.CACHE_DURATION;
  }

  /**
   * Get total cache size in bytes
   */
  private getTotalCacheSize(): number {
    let totalSize = 0;
    this.cache.forEach(item => {
      totalSize += item.size;
    });
    return totalSize;
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): CacheStats {
    return {
      totalSize: this.getTotalCacheSize(),
      itemCount: this.cache.size,
      hitRate: this.totalRequests > 0 ? this.cacheHits / this.totalRequests : 0,
      totalRequests: this.totalRequests,
      cacheHits: this.cacheHits,
    };
  }

  /**
   * Clear all cached images
   */
  clearCache(): void {
    // Revoke all object URLs to prevent memory leaks
    this.cache.forEach(item => {
      URL.revokeObjectURL(URL.createObjectURL(item.blob));
    });

    this.cache.clear();
    this.preloadQueue.clear();
    this.totalRequests = 0;
    this.cacheHits = 0;
  }

  /**
   * Remove specific image from cache
   */
  removeFromCache(url: string): void {
    const item = this.cache.get(url);
    if (item) {
      URL.revokeObjectURL(URL.createObjectURL(item.blob));
      this.cache.delete(url);
    }
  }

  /**
   * Check if an image is cached
   */
  isCached(url: string): boolean {
    const cached = this.cache.get(url);
    return cached ? this.isValidCacheEntry(cached) : false;
  }

  /**
   * Get cache size in a human-readable format
   */
  getFormattedCacheSize(): string {
    const size = this.getTotalCacheSize();
    const units = ['B', 'KB', 'MB', 'GB'];
    let unitIndex = 0;
    let formattedSize = size;

    while (formattedSize >= 1024 && unitIndex < units.length - 1) {
      formattedSize /= 1024;
      unitIndex++;
    }

    return `${formattedSize.toFixed(1)} ${units[unitIndex]}`;
  }
}

// Export singleton instance
export const imageCacheService = new ImageCacheService();
